import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import setNullEmptyValues from 'common/util/setNullEmptyValues';

import { pickAutocompleteFields } from 'server/models/Address/fieldgroups';

import {
  SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
  SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
} from 'api/actionTypes';

import { SR_API_ACTION_ADDRESS_REGISTER } from 'api/utilityActionTypes';

import { extractAddressFieldsFromGooglePlace } from 'client/lib/googlePlaces';

import {
  createInitialValues,
  createFormValidationFn,
  showFieldIf,
  hideFieldIf,
} from 'client/helpers/formikHelpers';

import postWithState from 'client/actions/postWithState';
import requestUtilityApi from 'client/actions/requestUtilityApi';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';

import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREEN,
  SR_BUTTON_HEIGHT_MEDIUM,
} from 'client/components/SrButton/SrButton';

import SrBox from 'client/components/SrBox';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';

const getFormFields = ({
  formValues,
  utilityPartnerId,
  utilityPartnerList,
  onFindGoogleListings,
  googleAddressMatches,
  setGoogleAddressMatches,
  onGoogleAddressChange,
} = {}) => {
  return [
    hideFieldIf(utilityPartnerId, {
      title: 'Utility Partner',
      id: 'utilityPartnerId',
      type: 'select',
      properties: {
        placeholder: '--',
        enableResetOption: true,
        choices: (utilityPartnerList || []).map(({ name, id }) => ({
          label: name,
          value: id,
        })),
      },
      validations: ['required'],
    }),
    showFieldIf(formValues?.utilityPartnerId, {
      id: 'addressInput',
      title: 'Address',
      type: 'autocomplete',
      properties: {
        placeholder: 'type address here...',
        choices: (googleAddressMatches || [])
          .filter(
            ({ place_id, description }) =>
              formValues.googlePlaceId !== place_id || formValues.addressInput !== description,
          )
          .map(({ description }) => description),
        onSuggestionsFetchRequested: ({ value }) => {
          return onFindGoogleListings({ addressInput: value });
        },
        onSuggestionSelected: ({ newValue, formikBag }) => {
          onGoogleAddressChange({ newValue, formikBag });
          formikBag.setFieldValue('googlePlaceId', newValue);
          setGoogleAddressMatches([]);
        },
        filterFunc: () => {
          return true;
        },
        autoFocus: true,
        alwaysRenderSuggestions: !formValues?.googlePlaceId,
      },
    }),
    showFieldIf(formValues?.utilityPartnerId, {
      id: 'unitNumber',
      title: 'Unit Number',
      label: '(optional)',
      type: 'short_text',
      properties: {
        placeholder: 'Write here...',
      },
    }),
    {
      id: 'googlePlaceId',
      type: 'hidden',
    },
    {
      id: 'streetName',
      type: 'hidden',
    },
    {
      id: 'streetNumber',
      type: 'hidden',
    },
    {
      id: 'zip',
      type: 'hidden',
    },
    {
      id: 'city',
      type: 'hidden',
    },
    {
      id: 'county',
      type: 'hidden',
    },
    {
      id: 'stateCode',
      type: 'hidden',
    },
    {
      id: 'latitude',
      type: 'hidden',
    },
    {
      id: 'longitude',
      type: 'hidden',
    },
  ];
};

const SrUtilityProfitAdminRegisterAddressForm = ({
  dispatch,
  utilityPartnerId,
  utilityPartnerList,
  afterSubmit,
}) => {
  const [googleAddressMatches, setGoogleAddressMatches] = useState([]);

  const onFindGoogleListings = ({ addressInput }) => {
    if (!addressInput) {
      setGoogleAddressMatches([]);
      return;
    }

    dispatch(postWithState, {
      queryId: addressInput,
      action: SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
      payload: {
        input: addressInput,
        limitToTexas: false,
      },
    }).then((autocompletePredictions) => {
      setGoogleAddressMatches(autocompletePredictions || []);
    });
  };

  const onGoogleAddressChange = ({ newValue, formikBag }) => {
    const selectedMatch = googleAddressMatches?.find(({ description }) => description === newValue);
    if (selectedMatch?.place_id) {
      dispatch(postWithState, {
        action: SR_API_ACTION_GOOGLE_MAPS_GET_PLACE_DETAILS,
        payload: {
          placeId: selectedMatch.place_id,
        },
      }).then((googlePlace) => {
        const addressMatch = extractAddressFieldsFromGooglePlace(googlePlace);
        formikBag.setFieldValue('googlePlaceId', selectedMatch.place_id);
        formikBag.setFieldValue('streetName', addressMatch.streetName);
        formikBag.setFieldValue('streetNumber', addressMatch.streetNumber);
        formikBag.setFieldValue('zip', addressMatch.zip);
        formikBag.setFieldValue('city', addressMatch.city);
        formikBag.setFieldValue('county', addressMatch.county);
        formikBag.setFieldValue('stateCode', addressMatch.stateCode);
        formikBag.setFieldValue('latitude', parseFloat(addressMatch.latitude) || '');
        formikBag.setFieldValue('longitude', parseFloat(addressMatch.longitude) || '');
      });
    } else {
      formikBag.setFieldValue('googlePlaceId', '');
      formikBag.setFieldValue('streetName', '');
      formikBag.setFieldValue('streetNumber', '');
      formikBag.setFieldValue('zip', '');
      formikBag.setFieldValue('city', '');
      formikBag.setFieldValue('stateCode', '');
      formikBag.setFieldValue('latitude', '');
      formikBag.setFieldValue('longitude', '');
    }
  };

  const onSubmit = (formValues, formikBag) => {
    const {
      utilityPartnerId,
      googlePlaceId,
      streetName,
      streetNumber,
      zip,
      city,
      county,
      stateCode,
      unitNumber,
      latitude,
      longitude,
    } = formValues;

    dispatch(requestUtilityApi, {
      action: SR_API_ACTION_ADDRESS_REGISTER,
      payload: {
        utilityPartnerId: parseInt(utilityPartnerId, 10),
        address: setNullEmptyValues({
          googlePlaceId,
          streetName,
          streetNumber,
          zip,
          city,
          county,
          stateCode,
          unitNumber,
          latitude,
          longitude,
        }),
      },
      onSuccess(address) {
        formikBag.resetForm();

        if (afterSubmit) {
          afterSubmit(address);
        }

        if (!utilityPartnerId) {
          global.alert('Address registered successfully.');
        }
      },
      onError(error) {
        global.alert(error?.message);
      },
    });
  };

  const initialFormFields = getFormFields({
    utilityPartnerId,
    setGoogleAddressMatches,
  });

  return (
    <Formik
      initialValues={createInitialValues({
        formFields: initialFormFields,
        defaultValues: {
          utilityPartnerId,
        },
      })}
      enableReinitialize
      validate={createFormValidationFn(initialFormFields)}
      onSubmit={onSubmit}
      render={(formikBag) => {
        const formValues = formikBag.values;

        const autocompleteAddressFields = pickAutocompleteFields(formValues);
        const addressFieldsValid = Object.keys(autocompleteAddressFields)
          .filter((fieldId) => fieldId !== 'county')
          .every((fieldId) => autocompleteAddressFields[fieldId]);

        const formFields = getFormFields({
          formValues,
          utilityPartnerId,
          utilityPartnerList,
          onFindGoogleListings,
          googleAddressMatches,
          onGoogleAddressChange,
          setGoogleAddressMatches,
        });

        return (
          <Form>
            {formFields.map((formField) => (
              <SrFormikField
                key={formField.id}
                formField={formField}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            ))}
            <SrBox padding="28px 0 12px">
              <SrButton
                theme={SR_BUTTON_THEME_ADOBE_GREEN}
                disabled={!formikBag.isValid || !formikBag.dirty || !addressFieldsValid}
                heightType={SR_BUTTON_HEIGHT_MEDIUM}
                hasBoxShadow
                type="submit"
              >
                {utilityPartnerId ? 'Submit' : 'Register Address'}
              </SrButton>
            </SrBox>
            {autocompleteAddressFields.googlePlaceId && !addressFieldsValid && (
              <SrErrorMessage>
                Address is missing some fields. Please try searching for the address again.
              </SrErrorMessage>
            )}
            {autocompleteAddressFields.googlePlaceId && !utilityPartnerId && (
              <pre style={{ marginTop: '10px' }}>
                Address components
                {JSON.stringify(autocompleteAddressFields, null, 2)}
              </pre>
            )}
          </Form>
        );
      }}
    />
  );
};

SrUtilityProfitAdminRegisterAddressForm.propTypes = {
  utilityPartnerId: PropTypes.number,
  utilityPartnerList: PropTypes.array,
  afterSubmit: PropTypes.func,
  dispatch: PropTypes.func.isRequired,
};

SrUtilityProfitAdminRegisterAddressForm.displayName = 'SrUtilityProfitAdminRegisterAddressForm';

export default SrUtilityProfitAdminRegisterAddressForm;
